import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  getBbpsCategories, getBbpsBillers, fetchBbpsBill, payBbpsBill,
  getBbpsWallet, getBbpsTransactions, bulkPayBbps, getBbpsAccessStatus,
} from '../lib/api';
import PaymentCheckoutModal from '../components/payments/PaymentCheckoutModal';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;
const fmtDT = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const QUICK_AMOUNTS = [199, 299, 399, 499, 599, 799, 999, 1499];

const STATUS_STYLES = {
  success: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  failed: 'bg-red-100 text-red-700 border-red-300',
  pending: 'bg-amber-100 text-amber-700 border-amber-300',
};

export default function BbpsPage() {
  const [wallet, setWallet] = useState({ balance: 0, lifetime_debited: 0 });
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [billers, setBillers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [step, setStep] = useState('categories'); // categories | billers | pay | success | bulk | bulk-result
  const [selectedBiller, setSelectedBiller] = useState(null);
  const [customerId, setCustomerId] = useState('');
  const [amount, setAmount] = useState('');
  const [billFetch, setBillFetch] = useState(null);
  const [busy, setBusy] = useState('');
  const [paySuccess, setPaySuccess] = useState(null);
  const [access, setAccess] = useState({ loading: true, enabled: false, reason: '' });
  // Bulk paste state
  const [bulkPaste, setBulkPaste] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  // Payment checkout modal state
  const [checkout, setCheckout] = useState(null); // { amount, purpose, billContext } | null

  const loadWallet = useCallback(async () => {
    try { const res = await getBbpsWallet(); setWallet(res.data); } catch { /* wallet not created yet */ }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      setLoadingCats(true);
      const res = await getBbpsCategories();
      setCategories(res.data.categories || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Categories load fail');
    } finally { setLoadingCats(false); }
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      const res = await getBbpsTransactions({ limit: 20 });
      setTransactions(res.data.transactions || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadWallet(); loadCategories(); loadTransactions(); }, [loadWallet, loadCategories, loadTransactions]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getBbpsAccessStatus();
        setAccess({ loading: false, enabled: !!res.data.enabled, reason: res.data.reason || '' });
      } catch {
        setAccess({ loading: false, enabled: false, reason: 'error' });
      }
    })();
  }, []);

  const openCategory = async (cat) => {
    setActiveCategory(cat);
    setStep('billers');
    try {
      const res = await getBbpsBillers(cat.key);
      setBillers(res.data.billers || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Billers load fail');
    }
  };

  const selectBiller = (b) => {
    setSelectedBiller(b);
    setStep('pay');
    setCustomerId('');
    setAmount('');
    setBillFetch(null);
  };

  const doFetchBill = async () => {
    if (!customerId.trim()) { toast.error('Customer/Mobile ID daalein'); return; }
    try {
      setBusy('fetch');
      const res = await fetchBbpsBill({ biller_id: selectedBiller.biller_id, customer_id: customerId.trim() });
      setBillFetch(res.data);
      if (res.data.status === 'SUCCESS') {
        setAmount(String(res.data.amount || ''));
        toast.success(`Bill fetched: ${res.data.customer_name} · ${fmt(res.data.amount)}`);
      } else if (res.data.status === 'PLAN_BASED') {
        toast.info('Recharge amount enter karo — is category ke liye fetch nahi hoti');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fetch fail');
    } finally { setBusy(''); }
  };

  const doPay = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Valid amount daalein'); return; }
    if (amt > wallet.balance) { toast.error(`Wallet me sirf ${fmt(wallet.balance)} hai`); return; }
    if (!customerId.trim()) { toast.error('Customer ID daalein'); return; }
    try {
      setBusy('pay');
      const res = await payBbpsBill({
        biller_id: selectedBiller.biller_id,
        customer_id: customerId.trim(),
        amount: amt,
        customer_name: billFetch?.customer_name || '',
      });
      if (res.data.ok) {
        setPaySuccess(res.data.txn);
        setStep('success');
        toast.success('Payment successful ✓');
        await loadWallet();
        await loadTransactions();
      } else {
        toast.error(res.data.message || 'Payment failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Pay fail');
    } finally { setBusy(''); }
  };

  const resetToHome = () => {
    setStep('categories');
    setActiveCategory(null);
    setSelectedBiller(null);
    setCustomerId('');
    setAmount('');
    setBillFetch(null);
    setPaySuccess(null);
    setBulkPaste('');
    setBulkResult(null);
  };

  // Parse bulk paste — each line = "customer_id" OR "customer_id,amount" OR "customer_id\tamount"
  // Duplicates (same customer_id) are auto-removed — first occurrence wins.
  const parseBulkEntries = () => {
    const rows = bulkPaste.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const entries = [];
    const seen = new Set();
    let duplicatesRemoved = 0;
    for (const r of rows) {
      const parts = r.split(/[,\t|]|\s{2,}/).map(p => p.trim()).filter(Boolean);
      const custId = parts[0];
      const amt = parts[1] ? parseFloat(parts[1]) : null;
      if (!custId) continue;
      if (seen.has(custId)) { duplicatesRemoved += 1; continue; }
      seen.add(custId);
      entries.push({ customer_id: custId, ...(amt && !isNaN(amt) ? { amount: amt } : {}) });
    }
    entries.__duplicates_removed = duplicatesRemoved;
    return entries;
  };

  const bulkEntriesPreview = useMemo(() => parseBulkEntries(), [bulkPaste]);
  const bulkTotalProvided = bulkEntriesPreview.reduce((s, e) => s + (e.amount || 0), 0);
  const bulkAutoFetchCount = bulkEntriesPreview.filter(e => !e.amount).length;

  const doBulkPay = async () => {
    if (!selectedBiller) return;
    const entries = parseBulkEntries();
    if (entries.length === 0) { toast.error('Koi valid rows nahi'); return; }
    if (entries.length > 200) { toast.error('Max 200 rows'); return; }
    // Client-side sanity: if all amounts provided, check total vs wallet
    if (bulkAutoFetchCount === 0 && bulkTotalProvided > wallet.balance) {
      toast.error(`Total ₹${bulkTotalProvided.toFixed(0)} > wallet ₹${wallet.balance.toFixed(0)}`);
      return;
    }
    if (!window.confirm(`${entries.length} entries process karein? ${bulkAutoFetchCount > 0 ? `(${bulkAutoFetchCount} rows me amount auto-fetch hoga)` : `Total ₹${bulkTotalProvided.toFixed(0)}`}`)) return;
    try {
      setBusy('bulk');
      const res = await bulkPayBbps({ biller_id: selectedBiller.biller_id, entries });
      setBulkResult(res.data);
      setStep('bulk-result');
      toast.success(`${res.data.success}/${res.data.total} success — ₹${res.data.total_amount_charged.toFixed(0)} charged`);
      await loadWallet();
      await loadTransactions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk pay fail');
    } finally { setBusy(''); }
  };

  const paramLabel = selectedBiller?.params?.[0]?.label || 'Customer ID';
  const maxlen = selectedBiller?.params?.[0]?.maxlen || 20;
  const isPlanBased = ['mobile_prepaid', 'dth', 'fastag'].includes(selectedBiller?.category);

  const walletLow = wallet.balance < 100;
  const canPay = useMemo(() => {
    const a = parseFloat(amount);
    return a > 0 && a <= wallet.balance && customerId.trim().length > 0 && !busy;
  }, [amount, customerId, wallet.balance, busy]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50" data-testid="bbps-page">
      {/* ─── Access-Denied gate ─── */}
      {!access.loading && !access.enabled && (
        <div className="min-h-screen flex items-center justify-center p-6" data-testid="bbps-access-denied">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-4xl">🔒</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">BBPS Access Nahi Mila</h2>
            {access.reason === 'role_not_supported' ? (
              <p className="text-sm text-gray-600">Aapka role BBPS use nahi kar sakta.</p>
            ) : (
              <p className="text-sm text-gray-600">
                Admin ne aapko BBPS Bill Pay ka access grant nahi kiya hai. Please admin se contact karein
                aur access request karein.
              </p>
            )}
            <div className="pt-2">
              <a href="/" className="inline-block bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium">← Dashboard</a>
            </div>
          </div>
        </div>
      )}

      {access.enabled && <>
      {/* Hero — wallet card */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 px-4 py-6 shadow-lg">
        <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-white/80 text-xs uppercase tracking-widest font-medium">Bharat Bill Pay Wallet</p>
            <p className="text-white text-4xl font-bold tracking-tight mt-1" data-testid="wallet-balance">
              {fmt(wallet.balance)}
            </p>
            <p className="text-white/70 text-xs mt-1">
              Lifetime paid: {fmt(wallet.lifetime_debited)}
              {walletLow && <span className="ml-3 bg-red-500/40 text-white px-2 py-0.5 rounded text-[10px]">⚠ Low balance — admin se topup lo</span>}
            </p>
          </div>
          <button
            data-testid="bbps-home-btn"
            onClick={resetToHome}
            className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-full text-sm font-medium transition-all backdrop-blur-sm"
          >
            🏠 Home
          </button>
          <button
            data-testid="bbps-add-money-btn"
            onClick={() => setCheckout({ amount: 500, purpose: 'wallet_topup' })}
            className="bg-white text-purple-700 hover:bg-white/90 px-4 py-2 rounded-full text-sm font-semibold transition-all shadow"
          >
            💰 Add Money
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-6">
        {/* Breadcrumb */}
        {step !== 'categories' && (
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <button onClick={resetToHome} className="hover:text-purple-600">Home</button>
            <span>›</span>
            {activeCategory && (
              <>
                <button onClick={() => setStep('billers')} className="hover:text-purple-600">{activeCategory.label}</button>
                {selectedBiller && <><span>›</span><span className="text-gray-900 font-medium">{selectedBiller.biller_name}</span></>}
              </>
            )}
          </div>
        )}

        {/* STEP: categories */}
        {step === 'categories' && (
          <>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Bill Pay karein, jaldi se</h2>
              <p className="text-sm text-gray-600">Recharge · Bijli · Gas · FASTag · Credit Card · aur bahut kuch</p>
            </div>
            {loadingCats ? (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {[...Array(11)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl h-24 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="bbps-category-grid">
                {categories.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => openCategory(c)}
                    data-testid={`bbps-cat-${c.key}`}
                    className="bg-white hover:shadow-xl transition-all rounded-2xl p-4 flex flex-col items-center justify-center gap-2 border border-gray-100 hover:border-purple-300 hover:-translate-y-1"
                    style={{ borderTop: `3px solid ${c.color}` }}
                  >
                    <span className="text-4xl">{c.icon}</span>
                    <span className="text-xs font-semibold text-gray-800 text-center leading-tight">{c.label}</span>
                    <span className="text-[10px] text-gray-500">{c.biller_count} billers</span>
                  </button>
                ))}
              </div>
            )}

            {/* Recent transactions */}
            {transactions.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">Recent Transactions</h3>
                  <span className="text-xs text-gray-500">{transactions.length}</span>
                </div>
                <div className="space-y-2" data-testid="bbps-recent-txns">
                  {transactions.slice(0, 10).map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-lg shrink-0">
                          {categories.find(c => c.key === t.category)?.icon || '💳'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{t.biller_name}</p>
                          <p className="text-[10px] text-gray-500 font-mono truncate">
                            {t.customer_id} · {fmtDT(t.created_at)} · {t.bbps_ref_no || '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="text-sm font-semibold text-gray-900">{fmt(t.amount)}</span>
                        <span className={`text-[9px] border rounded px-1.5 py-0.5 uppercase tracking-wider ${STATUS_STYLES[t.status] || ''}`}>
                          {t.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* STEP: billers */}
        {step === 'billers' && activeCategory && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-5xl">{activeCategory.icon}</span>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{activeCategory.label}</h2>
                <p className="text-sm text-gray-600">{billers.length} biller{billers.length !== 1 ? 's' : ''} available</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="bbps-billers-grid">
              {billers.map((b) => (
                <button
                  key={b.biller_id}
                  onClick={() => selectBiller(b)}
                  data-testid={`bbps-biller-${b.biller_id}`}
                  className="bg-white hover:shadow-lg transition-all rounded-xl p-4 text-left border-2 border-transparent hover:border-purple-400"
                >
                  <p className="font-semibold text-gray-900">{b.biller_name}</p>
                  <p className="text-[10px] text-gray-500 mt-1 font-mono">{b.biller_id}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP: pay */}
        {step === 'pay' && selectedBiller && (
          <div className="max-w-2xl mx-auto space-y-4" data-testid="bbps-pay-form">
            <div className="bg-white rounded-2xl shadow-md p-6 space-y-4">
              <div className="flex items-start gap-3">
                <span className="text-5xl">{activeCategory?.icon || '💳'}</span>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900">{selectedBiller.biller_name}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{activeCategory?.label}</p>
                </div>
                <button
                  onClick={() => setStep('bulk')}
                  data-testid="bbps-bulk-toggle"
                  className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 px-3 py-1.5 rounded-full font-medium"
                >
                  📋 Bulk Paste
                </button>
              </div>

              {/* Customer ID */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{paramLabel}</label>
                <input
                  data-testid="bbps-customer-id"
                  type="text"
                  inputMode={/mobile|regex.*\\\\d/i.test(String(selectedBiller.params?.[0]?.regex || '')) ? 'numeric' : 'text'}
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value.slice(0, maxlen))}
                  placeholder={`e.g. ${'0'.repeat(Math.min(maxlen, 10))}`}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500 text-lg font-mono tracking-wider"
                />
              </div>

              {/* Fetch button (bill-based) */}
              {!isPlanBased && (
                <button
                  data-testid="bbps-fetch-btn"
                  onClick={doFetchBill}
                  disabled={busy === 'fetch' || !customerId.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                >
                  {busy === 'fetch' ? 'Fetching...' : '🔍 Fetch Bill'}
                </button>
              )}

              {/* Fetch result */}
              {billFetch && billFetch.status === 'SUCCESS' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm" data-testid="bbps-fetch-result">
                  <p className="font-semibold text-emerald-900">{billFetch.customer_name}</p>
                  <p className="text-xs text-emerald-700 mt-1">Bill Amount: <strong>{fmt(billFetch.amount)}</strong></p>
                  <p className="text-[10px] text-emerald-600 mt-0.5 font-mono">Ref: {billFetch.reference}</p>
                </div>
              )}

              {/* Amount */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                <input
                  data-testid="bbps-amount"
                  type="number"
                  min={1}
                  max={100000}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Amount enter karo"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500 text-lg font-bold"
                />
                {isPlanBased && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {QUICK_AMOUNTS.map((a) => (
                      <button
                        key={a}
                        onClick={() => setAmount(String(a))}
                        className="text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 px-2 py-1 rounded border border-purple-200"
                        data-testid={`bbps-quick-${a}`}
                      >
                        {fmt(a)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-3 flex justify-between text-sm">
                <span className="text-gray-600">Wallet Balance</span>
                <span className={`font-bold ${walletLow ? 'text-red-600' : 'text-gray-900'}`}>{fmt(wallet.balance)}</span>
              </div>

              {/* Insufficient balance banner + gateway options */}
              {parseFloat(amount) > 0 && parseFloat(amount) > wallet.balance && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2" data-testid="bbps-insufficient-banner">
                  <p className="text-xs text-amber-900 font-semibold">
                    ⚠ Wallet me kum balance hai ({fmt(wallet.balance)}). Shortfall: {fmt(parseFloat(amount) - wallet.balance)}
                  </p>
                  <div className="flex gap-2">
                    <button
                      data-testid="bbps-topup-btn"
                      onClick={() => setCheckout({ amount: Math.max(parseFloat(amount) - wallet.balance, 100), purpose: 'wallet_topup' })}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded text-xs font-medium"
                    >
                      💰 Add {fmt(Math.max(parseFloat(amount) - wallet.balance, 100))} to Wallet
                    </button>
                    <button
                      data-testid="bbps-direct-pay-btn"
                      onClick={() => setCheckout({
                        amount: parseFloat(amount),
                        purpose: 'direct_bill',
                        billContext: { biller_id: selectedBiller.biller_id, customer_id: customerId.trim(), customer_name: billFetch?.customer_name || '' },
                      })}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-xs font-medium"
                    >
                      ⚡ Pay {fmt(parseFloat(amount))} Direct (UPI/Card)
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-600">Razorpay · Cashfree · PayU · UPI accepted</p>
                </div>
              )}

              <button
                data-testid="bbps-pay-btn"
                onClick={doPay}
                disabled={!canPay}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-3 rounded-xl text-base font-bold shadow-lg transition-all"
              >
                {busy === 'pay' ? 'Processing...' : `Pay ${fmt(parseFloat(amount) || 0)} from Wallet`}
              </button>
              <p className="text-[10px] text-gray-500 text-center">Powered by Bharat BillPay · NPCI</p>
            </div>
          </div>
        )}

        {/* STEP: bulk paste */}
        {step === 'bulk' && selectedBiller && (
          <div className="max-w-3xl mx-auto space-y-4" data-testid="bbps-bulk-form">
            <div className="bg-white rounded-2xl shadow-md p-6 space-y-4">
              <div className="flex items-start gap-3">
                <span className="text-5xl">{activeCategory?.icon || '💳'}</span>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900">Bulk Paste — {selectedBiller.biller_name}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Ek line pe ek entry: <code className="bg-gray-100 px-1 rounded font-mono">consumer_id</code> ya <code className="bg-gray-100 px-1 rounded font-mono">consumer_id, amount</code></p>
                </div>
                <button
                  onClick={() => setStep('pay')}
                  className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full"
                >
                  ← Single
                </button>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Paste consumer numbers (max 200)</label>
                <textarea
                  data-testid="bbps-bulk-textarea"
                  value={bulkPaste}
                  onChange={(e) => setBulkPaste(e.target.value)}
                  rows={10}
                  placeholder={`Example:\n11111111111\n22222222222, 900\n33333333333\t950`}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500 font-mono text-sm"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Separator: comma, tab, pipe, ya 2+ spaces. Amount optional — na diya to auto-fetch hoga.
                </p>
              </div>

              {/* Preview */}
              {bulkEntriesPreview.length > 0 && (
                <div className="bg-slate-50 border rounded-lg p-3 space-y-2" data-testid="bbps-bulk-preview">
                  <div className="flex items-center justify-between text-xs flex-wrap gap-1">
                    <span className="font-semibold text-gray-800">{bulkEntriesPreview.length} unique rows detected</span>
                    <div className="flex gap-2 items-center flex-wrap">
                      {bulkEntriesPreview.__duplicates_removed > 0 && (
                        <span className="bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded font-semibold text-[10px]" data-testid="bbps-bulk-dupes-removed">
                          ⚠ {bulkEntriesPreview.__duplicates_removed} duplicate{bulkEntriesPreview.__duplicates_removed !== 1 ? 's' : ''} auto-removed
                        </span>
                      )}
                      {bulkAutoFetchCount > 0 && <span className="text-amber-700">{bulkAutoFetchCount} auto-fetch</span>}
                      {bulkTotalProvided > 0 && <span className="text-emerald-700">Given: {fmt(bulkTotalProvided)}</span>}
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto border rounded bg-white text-xs">
                    <table className="w-full">
                      <thead className="bg-slate-100 sticky top-0"><tr>
                        <th className="text-left px-2 py-1 font-mono text-[10px]">#</th>
                        <th className="text-left px-2 py-1 font-mono text-[10px]">Consumer ID</th>
                        <th className="text-right px-2 py-1 font-mono text-[10px]">Amount</th>
                      </tr></thead>
                      <tbody>
                        {bulkEntriesPreview.slice(0, 100).map((e, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-0.5 text-gray-500">{i + 1}</td>
                            <td className="px-2 py-0.5 font-mono">{e.customer_id}</td>
                            <td className="px-2 py-0.5 text-right font-mono">
                              {e.amount ? fmt(e.amount) : <span className="text-amber-600 text-[10px]">auto</span>}
                            </td>
                          </tr>
                        ))}
                        {bulkEntriesPreview.length > 100 && (
                          <tr><td colSpan={3} className="text-center text-gray-500 py-1 text-[10px]">... aur {bulkEntriesPreview.length - 100} rows</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Wallet check */}
              <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center text-sm">
                <div>
                  <p className="text-gray-600 text-xs">Wallet Balance</p>
                  <p className={`font-bold text-lg ${walletLow ? 'text-red-600' : 'text-gray-900'}`}>{fmt(wallet.balance)}</p>
                </div>
                {bulkAutoFetchCount === 0 && bulkTotalProvided > 0 && (
                  <div className="text-right">
                    <p className="text-gray-600 text-xs">Required</p>
                    <p className={`font-bold text-lg ${bulkTotalProvided > wallet.balance ? 'text-red-600' : 'text-emerald-700'}`}>
                      {fmt(bulkTotalProvided)}
                    </p>
                  </div>
                )}
              </div>

              {/* Insufficient balance for bulk → top-up wallet */}
              {bulkAutoFetchCount === 0 && bulkTotalProvided > 0 && bulkTotalProvided > wallet.balance && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2" data-testid="bbps-bulk-insufficient">
                  <p className="text-xs text-amber-900 font-semibold">
                    ⚠ Total {fmt(bulkTotalProvided)} &gt; Wallet {fmt(wallet.balance)}. Shortfall: {fmt(bulkTotalProvided - wallet.balance)}
                  </p>
                  <button
                    data-testid="bbps-bulk-topup-btn"
                    onClick={() => setCheckout({ amount: bulkTotalProvided - wallet.balance, purpose: 'wallet_topup' })}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded text-xs font-medium"
                  >
                    💰 Add Shortfall ({fmt(bulkTotalProvided - wallet.balance)}) to Wallet via UPI/Card
                  </button>
                  <p className="text-[10px] text-gray-600">Razorpay · Cashfree · PayU accepted</p>
                </div>
              )}

              <button
                data-testid="bbps-bulk-pay-btn"
                onClick={doBulkPay}
                disabled={busy === 'bulk' || bulkEntriesPreview.length === 0 || (bulkAutoFetchCount === 0 && bulkTotalProvided > wallet.balance)}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-3 rounded-xl text-base font-bold shadow-lg transition-all"
              >
                {busy === 'bulk'
                  ? `Processing ${bulkEntriesPreview.length} entries...`
                  : `📋 Bulk Pay ${bulkEntriesPreview.length} entries`}
              </button>
              <p className="text-[10px] text-gray-500 text-center">
                ⚠️ Strict check: total &gt; wallet balance hua to kuchh bhi charge nahi hoga
              </p>
            </div>
          </div>
        )}

        {/* STEP: bulk result */}
        {step === 'bulk-result' && bulkResult && (
          <div className="max-w-3xl mx-auto space-y-4" data-testid="bbps-bulk-result">
            <div className="bg-white rounded-2xl shadow-xl p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-[10px] text-emerald-700 uppercase font-semibold">Success</p>
                  <p className="text-2xl font-bold text-emerald-700">{bulkResult.success}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-[10px] text-red-700 uppercase font-semibold">Failed</p>
                  <p className="text-2xl font-bold text-red-700">{bulkResult.failed}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-[10px] text-blue-700 uppercase font-semibold">Charged</p>
                  <p className="text-xl font-bold text-blue-700">{fmt(bulkResult.total_amount_charged)}</p>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0"><tr className="text-[10px] uppercase text-gray-600">
                    <th className="px-2 py-1.5 text-left">#</th>
                    <th className="px-2 py-1.5 text-left">Consumer ID</th>
                    <th className="px-2 py-1.5 text-right">Amount</th>
                    <th className="px-2 py-1.5 text-left">Status</th>
                    <th className="px-2 py-1.5 text-left">BBPS Ref</th>
                  </tr></thead>
                  <tbody>
                    {(bulkResult.results || []).map((r) => (
                      <tr key={r.row} className="border-t">
                        <td className="px-2 py-1 text-gray-500">{r.row}</td>
                        <td className="px-2 py-1 font-mono">{r.customer_id}</td>
                        <td className="px-2 py-1 text-right font-semibold">{fmt(r.amount)}</td>
                        <td className="px-2 py-1">
                          <span className={`border rounded px-1.5 py-0.5 text-[9px] uppercase ${STATUS_STYLES[r.status] || ''}`}>{r.status}</span>
                          {r.reason && <span className="text-[9px] text-red-600 ml-1">{r.reason}</span>}
                        </td>
                        <td className="px-2 py-1 font-mono text-[10px] text-gray-600">{r.bbps_ref_no || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setBulkPaste(''); setBulkResult(null); setStep('bulk'); }}
                  className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-800 px-4 py-2 rounded-lg text-sm font-medium"
                  data-testid="bbps-bulk-again-btn"
                >
                  Ek aur bulk karo
                </button>
                <button
                  onClick={resetToHome}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  Home
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP: success */}
        {step === 'success' && paySuccess && (
          <div className="max-w-lg mx-auto" data-testid="bbps-success">
            <div className="bg-white rounded-2xl shadow-xl p-6 text-center space-y-4">
              <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-5xl">✓</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-emerald-700">Payment Successful</h2>
                <p className="text-sm text-gray-600 mt-1">Bhugtan safal ho gaya</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="font-bold text-lg text-gray-900">{fmt(paySuccess.amount)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Biller</span><span className="font-medium">{paySuccess.biller_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Customer</span><span className="font-mono">{paySuccess.customer_id}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">BBPS Ref</span><span className="font-mono text-xs">{paySuccess.bbps_ref_no}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Approval Code</span><span className="font-mono text-xs">{paySuccess.approval_code}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Txn ID</span><span className="font-mono text-[10px]">{paySuccess.txn_id}</span></div>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="bbps-again-btn"
                  onClick={() => { setStep('pay'); setCustomerId(''); setAmount(''); setBillFetch(null); setPaySuccess(null); }}
                  className="flex-1 bg-purple-100 hover:bg-purple-200 text-purple-800 px-4 py-2 rounded-lg text-sm font-medium"
                >
                  Ek aur karo
                </button>
                <button
                  data-testid="bbps-home-btn-success"
                  onClick={resetToHome}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  Home
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </>}

      {/* Payment Checkout Modal (Razorpay / Cashfree / PayU / Mock) */}
      {checkout && (
        <PaymentCheckoutModal
          open={!!checkout}
          onClose={() => setCheckout(null)}
          amount={checkout.amount}
          purpose={checkout.purpose}
          billContext={checkout.billContext}
          onSuccess={async (data) => {
            await loadWallet();
            await loadTransactions();
            if (data.purpose === 'direct_bill') {
              // Fake a success state matching the single-pay flow so user sees the ref
              setPaySuccess({
                txn_id: data.txn_id,
                amount: data.amount,
                biller_name: data.biller_name,
                customer_id: checkout.billContext?.customer_id || '',
                bbps_ref_no: data.bbps_ref_no,
                approval_code: '',
              });
              setStep('success');
            } else {
              toast.success(`Wallet credited ${fmt(data.credited)}. New balance: ${fmt(data.new_wallet_balance)}`);
            }
          }}
        />
      )}
    </div>
  );
}
